import useSWR from 'swr';
import { apiGetAuth } from '../../api';

// ============================================================================
// Types
// ============================================================================

interface OnlineUsersData {
  online_count: number;
  online_user_ids: number[];
  active_today: number;
  as_of: string;
}

interface FeatureUsageItem {
  feature: string;
  unique_users: number;
  total_events: number;
  avg_events_per_user: number;
}

interface FeatureUsageData {
  period_start: string;
  period_end: string;
  period_days: number;
  features: FeatureUsageItem[];
}

interface DailyActivityItem {
  date: string;
  unique_users: number;
  total_events: number;
  new_users: number;
}

interface ActivityTrendData {
  period_days: number;
  data: DailyActivityItem[];
}

interface UserActivityItem {
  id: number;
  action: string;
  details: Record<string, any> | null;
  ip_address: string | null;
  created_at: string;
}

interface UserActivityData {
  user_id: number;
  total: number;
  offset: number;
  limit: number;
  activities: UserActivityItem[];
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch currently online users count.
 * Polls every 30 seconds by default for real-time updates.
 *
 * @param refreshInterval - Polling interval in ms (default 30000)
 * @returns Online count, active today count, loading state
 *
 * @example
 * const { onlineCount, activeToday, isLoading } = useOnlineUsers();
 */
export function useOnlineUsers(refreshInterval = 30000) {
  const { data, error, mutate } = useSWR<OnlineUsersData>(
    '/admin/analytics/online-users?minutes=5',
    apiGetAuth,
    {
      refreshInterval,
      revalidateOnFocus: true,
    }
  );

  return {
    onlineCount: data?.online_count ?? 0,
    onlineUserIds: data?.online_user_ids ?? [],
    activeToday: data?.active_today ?? 0,
    asOf: data?.as_of,
    isLoading: !data && !error,
    error,
    mutate,
  };
}

/**
 * Hook to fetch feature usage statistics.
 * Shows which features are most/least used over the specified period.
 *
 * @param days - Number of days to analyze (default 7)
 * @returns Feature usage data, loading state
 *
 * @example
 * const { features, isLoading } = useFeatureUsage(30);
 * // features = [{ feature: 'Bet Builder', unique_users: 45, total_events: 230, ... }]
 */
export function useFeatureUsage(days = 7) {
  const { data, error, mutate } = useSWR<FeatureUsageData>(
    `/admin/analytics/feature-usage?days=${days}`,
    apiGetAuth,
    {
      refreshInterval: 300000, // 5 minutes
      revalidateOnMount: true,
    }
  );

  return {
    features: data?.features ?? [],
    periodStart: data?.period_start,
    periodEnd: data?.period_end,
    periodDays: data?.period_days ?? days,
    isLoading: !data && !error,
    error,
    mutate,
  };
}

/**
 * Hook to fetch daily activity trend data.
 * Shows unique users, events, and new signups per day.
 *
 * @param days - Number of days to analyze (default 30)
 * @returns Daily trend data, loading state
 *
 * @example
 * const { trendData, isLoading } = useActivityTrend(30);
 * // trendData = [{ date: '2024-01-15', unique_users: 12, total_events: 89, new_users: 2 }]
 */
export function useActivityTrend(days = 30) {
  const { data, error, mutate } = useSWR<ActivityTrendData>(
    `/admin/analytics/activity-trend?days=${days}`,
    apiGetAuth,
    {
      refreshInterval: 300000, // 5 minutes
      revalidateOnMount: true,
    }
  );

  return {
    trendData: data?.data ?? [],
    periodDays: data?.period_days ?? days,
    isLoading: !data && !error,
    error,
    mutate,
  };
}

/**
 * Hook to fetch activity timeline for a specific user.
 * Used in admin user detail view.
 *
 * @param userId - User ID to fetch activity for
 * @param limit - Max records per page (default 50)
 * @param offset - Pagination offset (default 0)
 * @returns User's activity timeline, total count, loading state
 *
 * @example
 * const { activities, total, isLoading } = useUserActivity(123);
 */
export function useUserActivity(userId: number | null, limit = 50, offset = 0) {
  const { data, error, mutate } = useSWR<UserActivityData>(
    userId ? `/admin/analytics/user/${userId}/activity?limit=${limit}&offset=${offset}` : null,
    apiGetAuth,
    {
      revalidateOnMount: true,
    }
  );

  return {
    activities: data?.activities ?? [],
    total: data?.total ?? 0,
    isLoading: userId !== null && !data && !error,
    error,
    mutate,
  };
}
